import { resolveApiBaseUrl } from './baseUrl';
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { BackendErrorBody } from './types';
import { tokenStorage } from '@/auth/tokenStorage';

/**
 * Clientes HTTP centralizados (Slice 14a → multi-base).
 *
 * El portal consume DOS microservicios distintos:
 *   - TenantManagement (`tenantManagementApi`): puerto default 7074. Maneja auth, tenants,
 *     CUIT profiles, certificados, credenciales API.
 *   - Invoicing (`invoicingApi`): puerto default 7080. Maneja facturas (Slice 14a → read-only).
 *
 * Ambas instancias comparten:
 *   - Authorization Bearer (mismo JWT del AuthContext).
 *   - X-Tenant-Id (mismo tenant del AuthContext).
 *   - 401 handler → onUnauthorized → logout (set por AuthContext).
 *
 * El token y tenantId viven en memoria del módulo (no localStorage, requisito de seguridad V1).
 * El AuthContext sincroniza estos values llamando setAuthToken/setTenantHeader/clearAuth.
 *
 * `apiClient` se mantiene como alias de `tenantManagementApi` para backward compat con código
 * pre-14a.
 */

let authToken: string | null = null;
let tenantId: string | null = null;
let cuitProfileId: string | null = null;
let onUnauthorized: (() => void) | null = null;
// Impersonación (Platform Admin): cuando hay token activo, las requests a las APIs de tenant
// salen como el tenant impersonado (token read-only, auditado por el backend) en lugar del JWT
// del admin. Efímero (en memoria) — un refresh termina la impersonación.
let impersonationToken: string | null = null;
let impersonationTenantId: string | null = null;

type TildeRequestConfig = InternalAxiosRequestConfig & {
  /** Marca si esta request salió con un Bearer válido en el interceptor. */
  tildeAuthenticatedRequest?: boolean;
  /** Token exacto enviado: evita que un 401 tardío de una sesión anterior cierre la nueva. */
  tildeBearerToken?: string;
};

export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Indica si el cliente HTTP tiene una sesión que habilita queries protegidas. */
export function hasAuthToken(): boolean {
  return Boolean(impersonationToken ?? authToken ?? tokenStorage.get());
}

/** Activa/desactiva la impersonación. Pasar `(null, null)` para salir. */
export function setImpersonation(token: string | null, impersonatedTenantId: string | null): void {
  impersonationToken = token;
  impersonationTenantId = impersonatedTenantId;
}

export function setTenantHeader(id: string | null): void {
  tenantId = id;
}

/**
 * Selecciona un CuitProfile activo para todas las requests subsiguientes (header opcional
 * `X-Cuit-Profile-Id`). Lo setea el CuitContext cuando el contador elige un CUIT específico
 * en el TopBar dropdown. `null` significa "Todos los CUITs" → no se envía header.
 *
 * V1: el backend NO valida ni filtra por este header — es preparación para V2 server-side
 * filter. El portal hace client-side filter sobre las listas (Invoices, Audit, etc.).
 */
export function setCuitProfileHeader(id: string | null): void {
  cuitProfileId = id;
}

export function clearAuth(): void {
  authToken = null;
  impersonationToken = null;
  impersonationTenantId = null;
  tenantId = null;
  cuitProfileId = null;
  tokenStorage.clear();
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

const env = import.meta.env as Record<string, string | undefined>;

// Cuando el admin está siendo servido detrás del Cloudflare Tunnel (dev.tildeafip.com),
// el browser corre en un host que NO es la máquina del dev. Pegarle a localhost:7074 desde
// ahí es la localhost del browser, no la del dev box — falla. Si no hay env override
// explícito, derivamos la URL pública de cada service a partir del hostname actual.
const isPublicStaging =
  typeof window !== 'undefined' && window.location.hostname.endsWith('tildeafip.com');

// TenantManagement: nueva var explícita > legacy var > public staging > default localhost:7074.
const tenantManagementRaw =
  env.VITE_TENANT_MANAGEMENT_BASE_URL ??
  env.VITE_API_BASE_URL ??
  (isPublicStaging ? 'https://tenant.tildeafip.com' : 'http://localhost:7074');

// Invoicing: solo nueva var explícita > public staging > default localhost:7080. NO usa
// VITE_API_BASE_URL como fallback porque sería incorrecto: apuntaría al TM.
const invoicingRaw =
  env.VITE_INVOICING_BASE_URL ??
  (isPublicStaging ? 'https://api.tildeafip.com' : 'http://localhost:7080');

const documentsRaw =
  env.VITE_DOCUMENTS_BASE_URL ??
  (isPublicStaging ? 'https://documents.tildeafip.com' : 'http://localhost:7085');

// InvoiceRecipients tiene Function App y PostgreSQL propios. En local usa el proxy same-origin
// de Vite. En una superficie pública la URL debe ser un origen canónico protegido por el edge;
// nunca se permite caer silenciosamente al hostname directo de la Function App, que bypass-earía
// WAF, cuotas y cualquier política del perímetro público.
const invoiceRecipientsBaseUrl = env.VITE_INVOICE_RECIPIENTS_BASE_URL?.trim();
if (isPublicStaging && !invoiceRecipientsBaseUrl) {
  throw new Error(
    'VITE_INVOICE_RECIPIENTS_BASE_URL es obligatoria en el portal público. ' +
      'Configure un hostname canónico detrás del perímetro de API.',
  );
}
const invoiceRecipientsRaw = invoiceRecipientsBaseUrl ?? 'http://localhost:7082';

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

function attachInterceptors(
  instance: AxiosInstance,
  endsSessionOnUnauthorized: boolean,
): AxiosInstance {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Session entry points authenticate with their own credentials, never an old portal grant.
    const path = (config.url ?? '').split('?')[0];
    const isSessionEntry = /\/(?:auth\/(?:login|oauth\/(?:Google|Microsoft)|magic\/(?:request|redeem)|email-verification\/(?:request|confirm)))\/?$/i.test(path);
    if (isSessionEntry) {
      config.headers.delete('Authorization');
      config.headers.delete('X-Tenant-Id');
      config.headers.delete('X-Cuit-Profile-Id');
      (config as TildeRequestConfig).tildeAuthenticatedRequest = false;
      (config as TildeRequestConfig).tildeBearerToken = undefined;
      return config;
    }
    // Si hay impersonación activa, el token + tenant del impersonado tienen prioridad.
    // `tokenStorage` is the stable session source. The module variable remains the fast path,
    // but the fallback is important during Vite HMR/remounts: an older API module instance can
    // survive while AuthContext has already populated the current in-memory session store.
    const effectiveToken = impersonationToken ?? authToken ?? tokenStorage.get();
    const effectiveTenant = impersonationToken ? impersonationTenantId : tenantId;
    (config as TildeRequestConfig).tildeAuthenticatedRequest = Boolean(effectiveToken);
    (config as TildeRequestConfig).tildeBearerToken = effectiveToken ?? undefined;
    if (import.meta.env.DEV && config.url && !config.url.includes('/auth/')) {
      console.info('[tilde-auth] request', {
        url: config.url,
        hasToken: Boolean(effectiveToken),
        tokenLength: effectiveToken?.length ?? 0,
        hasTenant: Boolean(effectiveTenant),
      });
    }
    if (effectiveToken) {
      config.headers.set('Authorization', `Bearer ${effectiveToken}`);
      // AxiosHeaders.set() es la ruta normal, pero la asignación explícita conserva el header
      // cuando un adapter/browser devuelve un objeto de headers plano durante una transición HMR.
      config.headers.Authorization = `Bearer ${effectiveToken}`;
    }
    if (effectiveTenant) {
      config.headers.set('X-Tenant-Id', effectiveTenant);
      config.headers['X-Tenant-Id'] = effectiveTenant;
    }
    if (cuitProfileId) {
      config.headers.set('X-Cuit-Profile-Id', cuitProfileId);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<BackendErrorBody>) => {
      // 401 → token expirado o inválido. Notifico al AuthContext.
      if (error.response?.status === 401) {
        // Evito loop si el request que falló es /auth/login (que devuelve 401 en credenciales malas).
        const isLogin = error.config?.url?.endsWith('/auth/login');
        // Un request anónimo puede haber quedado en vuelo antes de un login exitoso. No debe
        // invalidar el token recién adquirido cuando su 401 llega después del login.
        const hadAuthentication =
          (error.config as TildeRequestConfig | undefined)?.tildeAuthenticatedRequest === true;
        const requestToken = (error.config as TildeRequestConfig | undefined)?.tildeBearerToken;
        const currentToken = impersonationToken ?? authToken ?? tokenStorage.get();
        const failedCurrentSession = Boolean(requestToken) && requestToken === currentToken;
        // Sólo TenantManagement es autoridad de la sesión del portal. Un 401 de Invoicing puede
        // significar un audience/config desalineado o una indisponibilidad parcial y no debe
        // destruir una sesión que TenantManagement todavía considera válida.
        if (
          !isLogin &&
          hadAuthentication &&
          failedCurrentSession &&
          endsSessionOnUnauthorized &&
          onUnauthorized
        ) {
          onUnauthorized();
        }
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

export const tenantManagementApi: AxiosInstance = attachInterceptors(
  axios.create({
    baseURL: resolveApiBaseUrl(tenantManagementRaw, 'http://localhost:7074'),
    timeout: 30000,
    headers: COMMON_HEADERS,
  }),
  true,
);

export const invoicingApi: AxiosInstance = attachInterceptors(
  axios.create({
    baseURL: resolveApiBaseUrl(invoicingRaw, 'http://localhost:7080'),
    timeout: 30000,
    headers: COMMON_HEADERS,
  }),
  false,
);

export const invoiceRecipientsApi: AxiosInstance = attachInterceptors(
  axios.create({
    baseURL: resolveApiBaseUrl(invoiceRecipientsRaw, 'http://localhost:7082'),
    timeout: 30000,
    headers: COMMON_HEADERS,
  }),
  false,
);

export const documentsApi: AxiosInstance = attachInterceptors(
  axios.create({
    baseURL: resolveApiBaseUrl(documentsRaw, 'http://localhost:7085'),
    timeout: 30000,
    headers: COMMON_HEADERS,
  }),
  false,
);

/**
 * @deprecated Slice 14a separó los services. Usar `tenantManagementApi` o `invoicingApi`
 * explícitamente en cada api/*.ts.
 *
 * Mantenido como alias para no romper imports existentes (cuitProfiles.ts, certificates.ts,
 * apiCredentials.ts, tenants.ts, auth.ts apuntan todos al TM).
 */
export const apiClient: AxiosInstance = tenantManagementApi;

/**
 * Wrappea un AxiosError en un mensaje amigable para el usuario final.
 * Conserva el code original como `code` para que el caller pueda hacer routing condicional
 * (p.ej. login muestra UI distinta para multiple_tenants).
 */
export class ApiError extends Error {
  readonly status: number | undefined;
  readonly code: string | undefined;

  constructor(message: string, status: number | undefined, code: string | undefined) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) {
    return err;
  }
  if (axios.isAxiosError<BackendErrorBody>(err)) {
    const status = err.response?.status;
    const body = err.response?.data;
    const code = body?.error ?? body?.code ?? body?.title;
    const message = body?.message ?? body?.detail ?? body?.error ?? err.message ?? 'Error desconocido.';
    return new ApiError(message, status, code);
  }
  return new ApiError(err instanceof Error ? err.message : 'Error desconocido.', undefined, undefined);
}
